import React, { useEffect, useRef, useState } from 'react';
import { useLoadingStates } from 'hooks/use-loading.hook';
import { TablePaginationConfig } from 'ui-lib';

interface PaginationFetchResult<DATA_ROW> {
    data: DATA_ROW[];
    nextToken?: string | null;
}

interface WithPaginationProps<DATA_ROW> {
    fetchData: (params: {
        page?: number;
        pageSize?: number;
        nextToken?: string | null;
    }) => Promise<PaginationFetchResult<DATA_ROW>>;
    mode?: 'offset' | 'cursor';
    cacheKey?: string;
    pageSize?: number;
    enableShowTotal?: boolean;
}

export type PagedSource<DATA_ROW> = ReturnType<typeof usePagination<DATA_ROW>>;

export function usePagination<DATA_ROW>(props: WithPaginationProps<DATA_ROW>) {
    const { fetchData, cacheKey = 'default', pageSize = 50, enableShowTotal } = props;
    const { withLoading, errorMessages } = useLoadingStates();
    // Counted rather than a flag so an earlier request finishing doesn't end loading for a later one
    const [pendingRequests, setPendingRequests] = useState(0);

    const paginationMode = React.useMemo(() => (props.mode === 'offset' ? 'offset' : 'cursor'), [props.mode]);
    const [data, setData] = useState<DATA_ROW[]>([]);
    const [pagination, setPagination] = useState<{ current: number; pageSize: number; total: number }>({
        current: 1,
        pageSize: pageSize,
        total: 0,
    });
    const [displayPagination, setDisplayPagination] = useState<TablePaginationConfig>({
        pageSize: pageSize,
        showSizeChanger: false,
        current: 1,
    });

    const [hasMoreData, setHasMoreData] = useState(true);

    // Cache the fetched data
    const cacheRef = useRef<Map<string, PaginationFetchResult<DATA_ROW>>>(new Map());
    // Bumped on every refresh so responses from superseded requests are dropped
    const generationRef = useRef(0);

    const makeCacheId = (page: number) => {
        return `${cacheKey}-${page}`;
    }

    const getTotalFromCache = () => Array.from(cacheRef.current.values()).flatMap((res) => res.data).length;

    const loadData = (page = 1, pageSize = 10, forceRefresh = false) => {
        const cacheId = makeCacheId(page);
        const cached = cacheRef.current.get(cacheId);

        if (cached && !forceRefresh) {
            setPagination((prev) => ({ ...prev, current: page, total: getTotalFromCache() }));
            return;
        }

        const generation = generationRef.current;
        setPendingRequests((count) => count + 1);
        return withLoading(async () => {
            const res = await fetchData({
                page: paginationMode === 'offset' ? page : undefined,
                pageSize,
                nextToken: paginationMode === 'cursor' ? cacheRef.current.get(makeCacheId(page - 1))?.nextToken : undefined,
            });

            if (generation !== generationRef.current) {
                return;
            }

            const fetched = res?.data ?? [];

            cacheRef.current.set(cacheId, res);

            setData(() => {
                // Replace the previous data with the new data
                return Array.from(cacheRef.current.values()).flatMap((res) => res.data).filter(Boolean);
            });

            // Each mode has different logic to determine if there is more data
            if (paginationMode === 'offset') {
                setHasMoreData(fetched.length != 0 && fetched.length == pageSize);
            } else {
                setHasMoreData(!!res.nextToken);
            }

            setPagination((prev) => {
                return { ...prev, current: page, total: getTotalFromCache() };
            });
        }).finally(() => setPendingRequests((count) => count - 1));
    };

    useEffect(() => {
        // Reset all data when mode changes
        refresh();
    }, [paginationMode]);

    useEffect(() => {
        setDisplayPagination((prev) => {
            return {
                ...prev,
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
            };
        });
    }, [pagination]);

    const handleTableChange = (paginationConfig: TablePaginationConfig, filters: any, sorter: any, extra: any) => {
        if (Object.keys(filters).some((key) => filters[key] !== null)) {
            // Use the current data source of the table to display
            setDisplayPagination({ showSizeChanger: false, pageSize: paginationConfig.pageSize, total: extra?.currentDataSource?.length });
            return;
        }
        // If there are no filters, loading data manually
        loadData(paginationConfig.current!, paginationConfig.pageSize!);
    };

    const refresh = async () => {
        generationRef.current += 1;
        cacheRef.current.clear();
        setData([]);
        loadData(1, pagination.pageSize!, true);
    };

    return {
        data,
        loading: pendingRequests > 0,
        errorMessages,
        pagination: {
            ...displayPagination,
            showTotal: enableShowTotal ? (total, range) => {
                if (hasMoreData) {
                    return `${range[0]}-${range[1] <= displayPagination.total ? range[1] : displayPagination.total} of ${displayPagination.total}+ items`;
                }
                return `${range[0]}-${range[1]} of ${displayPagination.total} items`;
            } : undefined,
            total: hasMoreData ? displayPagination.total + 1 : displayPagination.total,
        },
        onTableChange: handleTableChange,
        refresh,
        refreshPage: () => loadData(pagination.current!, pagination.pageSize!, true),
    };
}
